import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../config/queryClient';
import { createTasks, updateTaskDetails, deleteTask } from 'services/CaseServices';

export const useCreateTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data, is_lead = false }) => {
      const response = await createTasks(data, is_lead);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
};

export const useUpdateFullTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, data, isReminderUpdated = false, is_lead = false }) => {
      const response = await updateTaskDetails(taskId, null, data, isReminderUpdated, is_lead);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all task queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
};

export const useDeleteTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, caseId, pk, is_lead = false }) => {
      const response = await deleteTask(caseId, taskId, pk, is_lead);
      return response.data;
    },
    onMutate: async ({ taskId, caseId, pk, is_lead = false }) => {
      // Cancel all task-related queries to prevent race conditions
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks.all });

      // Store previous state of ALL task queries for rollback
      const previousQueries = [];
      queryClient.getQueriesData({ queryKey: queryKeys.tasks.all }).forEach(([key, data]) => {
        previousQueries.push({ key, data });
      });

      // Optimistically update ALL task queries (setQueriesData updates multiple queries)
      queryClient.setQueriesData({ queryKey: queryKeys.tasks.all }, (old) => {
        if (!old) return old;

        // Handle paginated/infinite query structure
        if (old.pages) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.filter((task) => task.task_id !== taskId),
              totalCount: Math.max(0, (page.totalCount || 0) - 1),
            })),
          };
        }

        // Handle simple array structure (if any queries use this)
        if (Array.isArray(old)) {
          return old.filter((task) => task.task_id !== taskId);
        }

        return old;
      });

      return { previousQueries };
    },
    onError: (err, variables, context) => {
      console.error('Delete task failed:', err);

      // Rollback all queries to their previous state
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ key, data }) => {
          queryClient.setQueryData(key, data);
        });
      }
    },
  });
};
